import React, { useCallback, useEffect, useRef, useState } from 'react';

import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import { playNextSong } from '@redux/player/slice';
import * as Sentry from '@sentry/browser';
import throttle from 'lodash/throttle';
import { useDispatch } from 'react-redux';
import { useBoolean } from 'react-use';

import BamOnlineLogoBlack from 'assets/logo-bam-online.svg';
import PlayerAreaEventsWrapper from 'components/shared/PlayerAreaEventsWrapper';
import SharedPlayerArea from 'components/shared/SharedPlayerArea';
import { shouldSendView } from 'services/helpers/view';
import { useCastingListener } from 'services/hooks/useCastingListener';
import { useMediaElementListener } from 'services/hooks/useMediaElementListener';
import { useUser } from 'services/hooks/useUser';
import { postCastingEvent } from 'services/networking/casting';
import { addB2cView } from 'services/networking/views';
import { SCOPE } from 'shared-types/constants';
import { Message as OfferMessage } from 'shared-types/user';

import {
  PlayButton,
  BamOnlineLogo,
  PlayerPlaceholderContainer,
  BamOnlineLogoBackground,
} from './B2cPlayerArea.style';
import Message from './components/Message';

const SEND_VIEW_FREQUENCY_IN_MS = 1000;

const PlayComponent: React.FC<{ handlePlay: () => void }> = ({ handlePlay }) => (
  <PlayButton onClick={handlePlay}>
    <PlayArrowRoundedIcon fontSize='inherit' cursor='pointer' />
  </PlayButton>
);

interface Props {
  // We pass an object here instead of directly the songId as a string
  // to trigger a rerender when the currentSong is updated, even when
  // the songId is the same: this is useful when playing twice the same
  // song
  song?: { id: string } | null;
  controls?: boolean;
  castingSessionId?: string | null;
  isCastingRemoteDevice: boolean;
  stripeId: string | null;
  offersMessageConfig?: OfferMessage;
  className?: string;
}

const B2cPlayerArea: React.FunctionComponent<Props> = ({
  song,
  controls = true,
  castingSessionId,
  isCastingRemoteDevice,
  offersMessageConfig,
  className,
}) => {
  const songViewSent = useRef(false);
  const [videoElement, setVideoElement] = useState<HTMLVideoElement | null>(null);
  const [isPlayButtonVisible, setIsPlayButtonVisible] = useBoolean(false);
  const dispatch = useDispatch();
  const currentUser = useUser();

  const handlePlay = useCallback(async () => {
    try {
      await videoElement?.play();
    } catch (error) {
      if (!controls) {
        setIsPlayButtonVisible(true);
      }
      console.log('Something wrong happened while playing video :');
      console.log(error);
      console.log(videoElement);
    }
  }, [videoElement, controls, setIsPlayButtonVisible]);

  const handleVideoEnd = () => {
    dispatch(playNextSong());
  };

  const throttledSendView = useCallback(
    throttle(async () => {
      if (shouldSendView(videoElement?.currentTime, song, songViewSent.current)) {
        // SongView is set by default to true because if we wait for the end of the request,
        // multiple requests could be sent in the meantime as this function is called every second
        songViewSent.current = true;
        try {
          await addB2cView(song.id, currentUser.userInfos?.username);
        } catch (error) {
          // only in the case of an error, do we set SongView back to false
          songViewSent.current = false;
          Sentry.captureException(error);
        }
      }
    }, SEND_VIEW_FREQUENCY_IN_MS),
    [song, songViewSent.current, videoElement, currentUser.userInfos],
  );

  useMediaElementListener(videoElement, 'timeupdate', () => {
    throttledSendView();
  });

  useEffect(() => {
    if (videoElement) {
      videoElement.currentTime = 0;
    }
    songViewSent.current = false;
  }, [song, videoElement]);

  useMediaElementListener(videoElement, 'play', () => {
    setIsPlayButtonVisible(false);
  });

  const commonProps = {
    song,
    activeVideoElement: videoElement,
    setInstrumentalVideoElement: setVideoElement,
    handlePlay,
    handleVideoEnd,
    controls,
    waitingList: [],
    isStorageActivated: false, // Only for B2B
  };

  return (
    <PlayerPlaceholderContainer className={className}>
      {!song || isCastingRemoteDevice ? (
        <BamOnlineLogoBackground>
          <BamOnlineLogo src={BamOnlineLogoBlack} />
          <Message offersMessageConfig={offersMessageConfig} />
        </BamOnlineLogoBackground>
      ) : castingSessionId ? (
        <PlayerAreaEventsWrapper
          {...commonProps}
          sendPlayerEvent={(eventType, data) => postCastingEvent(castingSessionId, eventType, data)}
          usePlayerEventsListener={useCastingListener}
          playButton={isPlayButtonVisible ? <PlayComponent handlePlay={handlePlay} /> : undefined}
          castingSessionId={castingSessionId}
          scope={SCOPE.B2C}
        />
      ) : (
        <SharedPlayerArea {...commonProps} scope={SCOPE.B2C} />
      )}
    </PlayerPlaceholderContainer>
  );
};

export default B2cPlayerArea;
